//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { useTikTokConfiguration } from '@account-settings/components/rights-requests/hooks/useTikTokConfiguration';
import Button from '@components/atoms/button/Button';
import { DefaultButtonStyles } from '@components/atoms/button/buttonStyles';
import { Select } from '@components/primitives/select/Select';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { useRightsConfigurationsQuery } from '@uia/rights/hooks/useRightsConfigurationsQuery';
import { checkIsStatusInitiated } from '@uia/rights/utils/statusUtils';
import { isDefined } from '@utils/filterPredicates';

export interface ChooseMessageSelectBoxProps {
  cuid: Cuid;
  requestType: RightsRequestOptions;
  selectedOption: IRightsOption | undefined;
  setSelectedOption: (value: IRightsOption) => void;
}

export const ChooseMessageSelectBox = ({
  cuid,
  requestType,
  selectedOption,
  setSelectedOption,
}: ChooseMessageSelectBoxProps) => {
  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: rightsOptions,
    isError: isErrorRightsConfigs,
    isLoading: isLoadingRightsConfigs,
  } = useRightsConfigurationsQuery(Cuid.toPartial(cuid), requestType);

  const {
    filteredRightsOptions,
    hasTikTokConfiguration,
    isCreatingConfig,
    isTikTokContent,
  } = useTikTokConfiguration({
    rightsOptions,
    socialNetwork: metadata?.socialNetwork,
    cuid: Cuid.toPartial(cuid),
    requestType,
  });

  const sortedRightsOptions = useMemo(() => {
    return filteredRightsOptions.sort((left, right) =>
      left.name.localeCompare(right.name),
    );
  }, [filteredRightsOptions]);

  const optionsLookup = useMemo(() => {
    return new Map(
      sortedRightsOptions.map((option) => [`${option.id}`, option]),
    );
  }, [sortedRightsOptions]);

  useEffect(() => {
    if (
      isTikTokContent &&
      hasTikTokConfiguration &&
      sortedRightsOptions.length > 0
    ) {
      const tikTokOption = sortedRightsOptions[0];
      if (
        tikTokOption &&
        (!selectedOption || selectedOption.id !== tikTokOption.id)
      ) {
        setSelectedOption(tikTokOption);
      }
    }
  }, [
    isTikTokContent,
    hasTikTokConfiguration,
    sortedRightsOptions,
    selectedOption,
    setSelectedOption,
  ]);

  if (isLoadingMetadata || isLoadingRightsConfigs || isCreatingConfig) {
    return null;
  }

  if (isErrorMetadata || isErrorRightsConfigs || !metadata) {
    return null;
  }

  const {
    rightsRequestStatus: { status },
  } = metadata;
  const areRightsAlreadyRequested = checkIsStatusInitiated(status);

  return (
    <Select
      disabled={areRightsAlreadyRequested}
      onValueChange={(value: string) => {
        const option = optionsLookup.get(value);
        if (isDefined(option)) {
          setSelectedOption(option);
        }
      }}
      value={`${selectedOption?.id}`}
    >
      <Select.Trigger asChild>
        <Trigger
          buttonStyles={DefaultButtonStyles}
          isDisabled={areRightsAlreadyRequested}
          isDropdown={true}
        >
          <Select.Value placeholder='Select value' />
        </Trigger>
      </Select.Trigger>
      <SelectContent align='start'>
        {sortedRightsOptions.map((option) => (
          <Select.Item key={option.id || option.name} value={`${option.id}`}>
            <Select.ItemText>{option.name}</Select.ItemText>
            <Select.Indicator />
          </Select.Item>
        ))}
      </SelectContent>
    </Select>
  );
};

const SelectContent = styled(Select.Content)(
  ({ theme }) => `
    max-height: ${theme.smallMeasure};
    width: 100%;
    z-index: ${theme.zindex.modal};
    overflow-y: auto;
  `,
);

const Trigger = styled(Button)(
  () => `
    width: 100%;
  `,
);
